import { defer } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import React, { useEffect, useRef, useState } from 'react';
import { BlinkDot } from '~/components/BlinkDot';
import { Preview } from '~/components/BlinkPreview';
import { BlinkQuotes } from '~/components/BlinkQuotes';
import BlinkUpdates from '~/components/BlinkUpdates';
import { Faq } from '~/components/Faq';
import { NavBar } from '~/components/NavBar';
import SectionHeading from '~/components/SectionHeading';
import { BlinkStats } from '~/components/stats/BlinkStats';
import { Timelines } from '~/components/Timeline';
import USPv2 from '~/components/USPv2';
import { getClicks } from '~/elasticsearch/queries/clicks.server';
import { getUniqueUsers } from '~/elasticsearch/queries/uniqueUsers.server';
import { PreviewContextProvider } from '~/utils/Context';
import { aggregateBlinkLiveData } from '~/utils/stats';
import { trackPageView } from '~/utils/tracking';
import type { LoaderArgs } from '@remix-run/node';
import type { BlinkLiveData } from '~/types';
import '~/styles/loading.css';
import '~/styles/preview.css';
import '~/styles/stats.css';
import '~/styles/fabric-expanded.css';
import '~/styles/blink.css';

export interface SectionRefs {
    howRef: React.RefObject<HTMLDivElement>;
    previewRef: React.RefObject<HTMLDivElement>;
    liveRef: React.RefObject<HTMLDivElement>;
    updatesRef: React.RefObject<HTMLDivElement>;
    faqRef: React.RefObject<HTMLDivElement>;
    quotesRef: React.RefObject<HTMLDivElement>;
}

export async function loader({ context }: LoaderArgs) {
    const previewFinncodes = await context.finncodeCache.get();
    const previewFinncode = previewFinncodes.length > 0 ? previewFinncodes[Math.floor(Math.random() * previewFinncodes.length)] : null;
    return defer({
        previewFinncode,
        blinkClicksLastMonth: getClicks(),
        uniqueUsersBlink: getUniqueUsers(true),
        uniqueUsersNonBlink: getUniqueUsers(false),
    });
}

export default function Index() {
    const data = useLoaderData<typeof loader>();

    const [id, setID] = useState<string>('');

    const [aggregatedLiveData, setAggregatedLiveData] = useState<BlinkLiveData>({});

    // useEffect to make it run client-side only
    useEffect(() => {
        const getLatestDataLoop = () => {
            fetch('/blink/newstats')
                .then((v) => {
                    return v.json();
                })
                .then((liveData) => {
                    //guard against duplicate id-messages
                    if (liveData.uuid === id) {
                        return;
                    }
                    setID(liveData.uuid);
                    setAggregatedLiveData((prevData) => aggregateBlinkLiveData(prevData, liveData.value));
                });
            setTimeout(getLatestDataLoop, 500);
        };
        getLatestDataLoop();
    }, []);

    const sectionRefs: SectionRefs = {
        howRef: useRef<HTMLDivElement>(null),
        previewRef: useRef<HTMLDivElement>(null),
        liveRef: useRef<HTMLDivElement>(null),
        updatesRef: useRef<HTMLDivElement>(null),
        faqRef: useRef<HTMLDivElement>(null),
        quotesRef: useRef<HTMLDivElement>(null),
    };

    useEffect(() => {
        trackPageView();
    }, []);

    return (
        <main>
            <NavBar refs={sectionRefs} />
            <USPv2 />
            <div className="snap-start relative">
                <div className="bg-blue-alt-900 m-auto text-center text-green-50" ref={sectionRefs.howRef}>
                    <div className="card-content mx-auto flex h-full flex-col px-16 py-128">
                        <SectionHeading heading={'Slik fungerer Blink'}>
                            <BlinkDot />
                            <BlinkDot backgroundColor={'green-100'} width={24} />
                            <BlinkDot width={24} />
                            <BlinkDot backgroundColor={'green-100'} />
                        </SectionHeading>
                        <Timelines />
                    </div>
                </div>
                <div className="text-blue-alt-900 relative m-auto bg-green-50 text-center" ref={sectionRefs.previewRef}>
                    <div className="card-content mx-auto flex w-screen flex-col pt-128">
                        <SectionHeading heading={'Forhåndsvisning'}>
                            <BlinkDot backgroundColor={'blue-alt-900'} />
                            <BlinkDot />
                            <BlinkDot width={24} />
                            <BlinkDot backgroundColor={'blue-alt-900'} />
                        </SectionHeading>
                    </div>
                    <Preview initialAd={data.previewFinncode ?? undefined} />
                </div>
                <div className="bg-green-50" ref={sectionRefs.liveRef}>
                    <div className="card-content mx-auto h-full py-128 text-center">
                        <SectionHeading heading={'Blink live'}>
                            <BlinkDot height={16} width={16} />
                            <BlinkDot height={16} width={44} backgroundColor="green-200" />
                            <BlinkDot backgroundColor="blue-alt-900" />
                            <BlinkDot height={16} width={16} />
                        </SectionHeading>
                        <BlinkStats
                            blinkClicksLastMonth={data.blinkClicksLastMonth}
                            uniqueUsersBlink={data.uniqueUsersBlink}
                            uniqueUsersNonBlink={data.uniqueUsersNonBlink}
                            aggregatedData={aggregatedLiveData}
                        />
                    </div>
                </div>

                <div className="h-vh bg-blue-alt-900 m-auto text-center text-green-50" ref={sectionRefs.updatesRef}>
                    <div className="card-content mx-auto flex h-full flex-col pt-128">
                        <SectionHeading heading={'Nyheter'}>
                            <BlinkDot backgroundColor="green-100" />
                            <BlinkDot width={44} />
                        </SectionHeading>
                        <PreviewContextProvider previewAdId={data.previewFinncode ?? undefined}>
                            <BlinkUpdates />
                        </PreviewContextProvider>
                    </div>
                </div>

                <div className="bg-green-50 " ref={sectionRefs.quotesRef}>
                    <div className="card-content mx-auto h-full py-128">
                        <SectionHeading heading={'Hva sier folk om Blink?'}>
                            <BlinkDot width={44} />
                            <BlinkDot width={44} />
                            <BlinkDot backgroundColor="blue-alt-900" />
                            <BlinkDot width={44} />
                        </SectionHeading>
                        <BlinkQuotes />
                    </div>
                </div>

                <div className="bg-blue-alt-900 m-auto pb-64 pt-64 text-center text-green-50" ref={sectionRefs.faqRef}>
                    <div className="card-content mx-auto h-full md:py-128">
                        <SectionHeading heading={'FAQ'}>
                            <BlinkDot />
                            <BlinkDot backgroundColor={'green-100'} width={24} />
                        </SectionHeading>
                        <Faq />
                    </div>
                </div>
            </div>
        </main>
    );
}
